/* WHAT A PERSON MAY DO, AS OPPOSED TO WHAT SEAT THEY SIT IN.
 *
 * Until now a seat WAS the permission: a Coach could not see a price because the code
 * asked whether they were a Coach. That is a good default and a bad ceiling - a gym
 * with a head coach who also does the billing had nowhere to put them, and the answer
 * "make them a Manager" hands over the whole console to fix one column.
 *
 * So a seat is now a PRESET rather than a rule. Every gate in the product asks
 * `can(who, permission)`. A seat answers that question by default; an Owner may
 * overrule it for one person.
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO.
 *
 * `null` permissions means "whatever this seat means", and that is what every existing
 * account has. Nothing changes for anybody until somebody is explicitly given a
 * different set - the migration writes no rows, and a gym that never opens the screen
 * behaves exactly as it did.
 *
 * And a permission cannot widen a seat's TENANCY. None of these cross a gym boundary;
 * they decide what you may do inside the one gym your login belongs to. Who may cross
 * gyms is a different question with a different answer (hq.ts), and it is deliberately
 * not expressible here.
 */
/* THE CATALOGUE. Each one is a gate that already existed in the product, so this list
   is a description of the code rather than a wish. Adding to it means adding a gate;
   it is not a place to park ideas. */
export const PERMISSIONS = [
    /** Plans, prices, renewal dates, and what a lead is worth. SITE-RULES: a coach who
     *  knows this member is on 549 and that one on 199 treats them differently. */
    'commercials',
    /** The Worth & value screen: what a member pays beside what they bring. */
    'worth',
    /** The gym's revenue, on the dashboard. */
    'revenue',
    /** Create, rename, re-seat, reset and remove staff logins - and set these
     *  permissions on them. Bounded further by grantableBy(): a seat can never hand out
     *  a seat above its own. */
    'accounts',
    /** Sign off the changes waiting in the approvals queue. */
    'approvals',
    /** Build and run campaigns. */
    'campaigns',
    /** Change the gym's own settings. */
    'settings',
    /** Change a member's status, plan or price - the facts that decide whether somebody
     *  is a member and what they pay. Without it the rest of a member's record is still
     *  editable: their goal, why they are here, who coaches them. */
    'member_edit',
];
const ALL = [...PERMISSIONS];
/* WHAT EACH SEAT MEANS WHEN NOBODY HAS SAID OTHERWISE. These reproduce exactly what
   the product did before permissions existed, which is the point: switching the
   mechanism on must not move a single person's access. */
export const SEAT_PRESETS = {
    gym_admin: ALL,
    /* A manager runs the floor and the money, and may hand out the two floor seats -
       grantableBy() is what stops that becoming "and may make themselves an Owner". */
    manager: [
        'commercials', 'worth', 'revenue', 'accounts', 'approvals', 'campaigns',
        /* A manager could always edit the gym's profile: there was no gate on
           PATCH /settings at all, and only the one VISIBILITY key was owner-only
           (canSetVisibility, which still applies underneath this and is finer). Leaving
           `settings` out here would have been the mechanism quietly taking something
           away, which is the one thing it must not do. */
        'settings',
        'member_edit',
    ],
    /* The floor. A support coach's own book, and a coach's own sessions. Neither is
       shown what anybody pays, which is the rule this whole file has to keep able to
       express. */
    sales: [],
    coach: [],
    /* Not a gym seat. It holds no gym permission at all: the HQ seat administers gyms
       and never reads inside one. See hq.ts. */
    super_admin: [],
    /* LEGACY IMPORT ROLES, carried in the Role union because rows in the v1 data hold
       them. They were never seats anybody sat in and they grant nothing. Listed rather
       than defaulted so that adding a role to the union forces a decision here. */
    datacleanup: [],
    phase1: [],
};
/** What this person may do: their explicit set if they have one, otherwise their
 *  seats'. Seats union rather than intersect - somebody who is both a Coach and a
 *  Manager is a Manager who also teaches. */
export function permissionsOf(roles, explicit) {
    if (explicit) {
        /* An unknown string is dropped rather than trusted. A permission this build does
           not have is either a rename we have not finished or a row somebody hand-edited,
           and neither should grant anything. */
        const known = explicit.filter((p) => ALL.includes(p));
        return [...new Set(known)];
    }
    const out = new Set();
    for (const r of roles ?? [])
        for (const p of SEAT_PRESETS[r] ?? [])
            out.add(p);
    return [...out];
}
/** The one question every gate in the product asks. */
export function can(roles, explicit, permission) {
    return permissionsOf(roles, explicit).includes(permission);
}
/** Does this person sit on their seats' defaults, or has somebody changed them? Used
 *  by the screen to say so, because "custom" is worth seeing on a staff list. */
export function isDefaultFor(roles, explicit) {
    if (!explicit)
        return true;
    const a = [...permissionsOf(roles, null)].sort();
    const b = [...permissionsOf(roles, explicit)].sort();
    return a.length === b.length && a.every((x, i) => x === b[i]);
}
/* WHAT AN OWNER MAY NOT GIVE AWAY. An Owner must keep `accounts`, or they have just
   locked themselves out of the screen that would undo it - the same rule that already
   stops a gym losing its last Owner, one level down. */
export const PERMANENT_FOR_OWNER = ['accounts'];
/** Human wording, for the one screen that lists these. Kept beside the catalogue so a
 *  new permission cannot ship without one. */
export const PERMISSION_LABELS = {
    commercials: {
        name: 'See plans and prices',
        help: 'What a member pays, renewal dates, and what a lead is worth. Off for the floor by default.',
    },
    worth: { name: 'Worth and value', help: 'The screen that sets what a member pays beside what they bring.' },
    revenue: { name: 'See the gym’s revenue', help: 'The monthly figure on the dashboard.' },
    accounts: { name: 'Manage staff logins', help: 'Add, re-seat, reset and remove staff, and set what they can do.' },
    approvals: { name: 'Sign off approvals', help: 'Decide the changes waiting in the approvals queue.' },
    campaigns: { name: 'Run campaigns', help: 'Build a campaign and point it at a segment.' },
    settings: { name: 'Change gym settings', help: 'The gym’s own profile, and message sending.' },
    member_edit: {
        name: 'Change a member’s plan, price or status',
        help: 'The facts that decide whether somebody is a member and what they pay. Everything else on a member stays editable without it.',
    },
};
